/**
 * Tracks the premium upsell banner funnel: Seen → Tapped, and carries the
 * banner's attribution into the purchase flow. The tap persists a descriptor
 * to localStorage so attribution survives the sign-in and payment redirects
 * (30 min expiry — an expired descriptor means the purchase goes unattributed,
 * never falsely credited).
 */

import mixpanelService from '../mixpanel-service'
import { generateUUID } from '@utils/uuid'
import localSave from '@utils/local-save'
import {
    EVENTS,
    PROPS,
    STORAGE_KEYS,
    CORRELATION_ID_EXPIRY_MS,
    bannerAttributionFromSource
} from './analytics-spec'

class BannerTracker {
    constructor() {
        if (BannerTracker.instance) {
            return BannerTracker.instance
        }
        BannerTracker.instance = this

        this._impressionId = null
        this._impressionSource = null
    }

    /**
     * Track a banner actually scrolling into view (caller enforces
     * once-per-page-visit). Starts a new impression.
     */
    onBannerSeen(source) {
        this._impressionId = generateUUID()
        this._impressionSource = source
        mixpanelService.track(EVENTS.PREMIUM_BANNER_SEEN, this._bannerProps(source))
    }

    /**
     * Track a tap on the banner's Go Premium button, linked to the current
     * impression, and persist the attribution for the purchase events.
     */
    onBannerTapped(source) {
        if (!this._impressionId || this._impressionSource !== source) {
            this._impressionId = generateUUID()
            this._impressionSource = source
        }
        mixpanelService.track(EVENTS.PREMIUM_BANNER_TAPPED, this._bannerProps(source))
        this._persistAttribution(source)
    }

    /**
     * Banner attribution props for purchase_started / purchase_succeeded —
     * {} when the journey didn't start at a banner (or the window expired).
     */
    getPurchaseAttributionProps() {
        const stored = this._readAttribution()
        if (!stored) return {}
        return this._attributionProps(stored)
    }

    clearAttribution() {
        try {
            localSave.removeItem(STORAGE_KEYS.BANNER_ATTRIBUTION)
        } catch (e) {
            // Ignore storage errors
        }
    }

    _bannerProps(source) {
        const { surface, channel } = bannerAttributionFromSource(source)
        return this._attributionProps({ impressionId: this._impressionId, surface, channel })
    }

    // The frozen banner dimensions shared by the funnel events (seen/tapped)
    // and the purchase events; radio_channel_name only rides along for
    // per-channel surfaces.
    _attributionProps({ impressionId, surface, channel }) {
        return {
            [PROPS.BANNER_IMPRESSION_ID]: impressionId,
            [PROPS.BANNER_SURFACE]: surface,
            ...(channel ? { [PROPS.RADIO_CHANNEL_NAME]: channel } : {})
        }
    }

    _persistAttribution(source) {
        const { surface, channel } = bannerAttributionFromSource(source)
        try {
            localSave.setItem(STORAGE_KEYS.BANNER_ATTRIBUTION, JSON.stringify({
                impressionId: this._impressionId,
                surface,
                channel,
                expiry: Date.now() + CORRELATION_ID_EXPIRY_MS
            }))
        } catch (e) {
            // Ignore storage errors
        }
    }

    _readAttribution() {
        try {
            const data = localSave.getItem(STORAGE_KEYS.BANNER_ATTRIBUTION)
            if (!data) return null

            const parsed = JSON.parse(data)
            if (parsed.expiry > Date.now()) {
                return parsed
            }
            localSave.removeItem(STORAGE_KEYS.BANNER_ATTRIBUTION)
        } catch (e) {
            // Ignore parse errors
        }
        return null
    }

    _resetForTesting() {
        this._impressionId = null
        this._impressionSource = null
    }
}

export default new BannerTracker()
